import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Paper,
  Typography,
} from '@mui/material';
import {
  Work as WorkIcon,
  Fullscreen as RestoreIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useUIStore } from '../../store/uiStore';

interface MinimizedJobApplicationWidgetProps {
  isVisible: boolean;
  onRestore: () => void;
  hasUnfinishedApplication?: boolean;
}

const MinimizedJobApplicationWidget: React.FC<MinimizedJobApplicationWidgetProps> = ({
  isVisible,
  onRestore,
  hasUnfinishedApplication = false,
}) => {
  const { selectedJobsCount, clearSelectedJobs, syncSelectedJobsCount } = useUIStore();
  const [showAddedAnimation, setShowAddedAnimation] = useState(false);
  const [previousCount, setPreviousCount] = useState(0);

  // Sync count on mount to ensure accuracy
  useEffect(() => {
    syncSelectedJobsCount();
  }, [syncSelectedJobsCount]);

  // Trigger animation when count increases
  useEffect(() => {
    if (selectedJobsCount > previousCount) {
      setShowAddedAnimation(true);
      setTimeout(() => setShowAddedAnimation(false), 1000);
    }
    setPreviousCount(selectedJobsCount);
  }, [selectedJobsCount, previousCount]);
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ 
            opacity: 0, 
            scale: 0.8, 
            x: 100,
            y: -100,
          }}
          animate={{ 
            opacity: 1, 
            scale: 1, 
            x: 0,
            y: 0,
          }}
          exit={{ 
            opacity: 0, 
            scale: 0.8, 
            x: 100,
            y: -100,
          }}
          transition={{
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94],
            scale: {
              type: "spring",
              damping: 20,
              stiffness: 300,
            },
            x: {
              type: "spring",
              damping: 25,
              stiffness: 400,
            },
            y: {
              type: "spring",
              damping: 25,
              stiffness: 400,
            },
          }}
          style={{
            position: 'fixed',
            top: 80, // Position a bit higher
            right: 20,
            zIndex: 9999,
          }}
        >
          <motion.div
            animate={showAddedAnimation ? {
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0],
            } : {}}
            transition={{
              duration: 0.6,
              ease: "easeInOut"
            }}
          >
            <Paper
              elevation={8}
              sx={{
                borderRadius: 3,
                background: showAddedAnimation 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.5) 0%, rgba(5, 150, 105, 0.5) 100%)'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.5) 0%, rgba(99, 102, 241, 0.5) 100%)',
                p: 1,
                boxShadow: showAddedAnimation
                  ? '0 10px 25px -5px rgba(16, 185, 129, 0.2), 0 4px 6px -2px rgba(16, 185, 129, 0.1)'
                  : '0 10px 25px -5px rgba(139, 92, 246, 0.15), 0 4px 6px -2px rgba(139, 92, 246, 0.05)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                opacity: 0.6,
                '&:hover': {
                  opacity: 1,
                  background: showAddedAnimation 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  boxShadow: showAddedAnimation
                    ? '0 10px 25px -5px rgba(16, 185, 129, 0.4), 0 4px 6px -2px rgba(16, 185, 129, 0.2)'
                    : '0 10px 25px -5px rgba(139, 92, 246, 0.3), 0 4px 6px -2px rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                {/* Count Display */}
                {selectedJobsCount > 0 && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <Box
                      sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        px: 1.5,
                        py: 0.5,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {showAddedAnimation && <CheckIcon sx={{ fontSize: 12 }} />}
                        {selectedJobsCount} job{selectedJobsCount !== 1 ? 's' : ''} chosen
                      </Typography>
                      {/* Temporary debug button */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSelectedJobs();
                        }}
                        sx={{
                          color: 'white',
                          fontSize: '0.6rem',
                          minWidth: 'auto',
                          width: 16,
                          height: 16,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          },
                        }}
                      >
                        ×
                      </IconButton>
                    </Box>
                  </motion.div>
                )}

                <Tooltip 
                  title="Restore Job Application Writer" 
                  placement="left"
                  arrow
                >
                  <IconButton
                    onClick={onRestore}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      width: 48,
                      height: 48,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        transform: 'scale(1.05)',
                        boxShadow: showAddedAnimation
                          ? '0 8px 20px -5px rgba(16, 185, 129, 0.4)'
                          : '0 8px 20px -5px rgba(139, 92, 246, 0.4)',
                      },
                      '&:active': {
                        transform: 'scale(0.95)',
                      },
                    }}
                  >
                    <WorkIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MinimizedJobApplicationWidget;
